import type { AnswerValue, Field } from '@/lib/intake/catalogue';
import { COPY } from './copy';

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/**
 * "Mar 14, 2021" from 2021-03-14, without the browser's time zone moving the day.
 * A date that does not exist (2026-02-30) is shown as typed rather than rolled
 * over into March, as the summary shows it (display-cases.json).
 */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return iso;
  return MONTH.format(date);
}

/**
 * An answer as a person reads it: labels, not option keys. Null when there is none.
 * The summary uses `intake/Display.java`; both run display-cases.json.
 */
export function displayAnswer(field: Field, value: AnswerValue | undefined): string | null {
  if (value === undefined) return null;
  const label = (key: string) => field.options?.find((o) => o.key === key)?.label ?? key;
  switch (field.type) {
    case 'multi':
      return Array.isArray(value) && value.length > 0 ? value.map(label).join('; ') : null;
    case 'select':
      return typeof value === 'string' && value !== '' ? label(value) : null;
    case 'yesno':
      return value === 'yes' ? COPY.yes : value === 'no' ? COPY.no : null;
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? formatIsoDate(value) : null;
    default:
      return typeof value === 'string' && value.trim() !== '' ? value : null;
  }
}
